/**
 * 承認フロー設定画面 (AdminApprovalFlow)
 *
 * 保存方式: per-プロセス明示保存が正。
 * spec L293/L310 の「即時自動保存」は旧記述であり、設計でオーバーライド済み。
 * 各プロセスの保存ボタンを押すことで明示的に保存する。
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react'

import { Code, ConnectError } from '@connectrpc/connect'
import {
  ApprovalMode,
  BusinessProcess,
} from '@ebalance/proto/bfftypes/v1/approval_flow_type_pb'
import type { AutoBidRule } from '@ebalance/proto/bffuser/v1/approval_flow_model_pb'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink, useSearchParams } from 'react-router'

import { Box, Flex, Link, Text, toaster } from '@enechain/ecloud-designsystem'

import { PageBody } from '~/components/common/PageBody'
import { PageIntro } from '~/components/common/PageIntro'
import { PageLayout } from '~/components/common/PageLayout'
import { PageScrollArea } from '~/components/common/PageScrollArea'
import { useUnsavedGuard } from '~/hooks/useUnsavedGuard'
import { ProcessAccordion } from '~/pages/AdminApprovalFlow/components/ProcessAccordion'
import { UnsavedLeaveDialog } from '~/pages/AdminApprovalFlow/components/UnsavedLeaveDialog'
import {
  PROCESS_DEFS,
  makeNoSectionCellKey,
} from '~/pages/AdminApprovalFlow/constants/processMap'
import { useGetApprovalFlowScreen } from '~/pages/AdminApprovalFlow/hooks/useGetApprovalFlowScreen'
import { useSaveApprovalFlow } from '~/pages/AdminApprovalFlow/hooks/useSaveApprovalFlow'
import {
  groupAutoBidRulesByArea,
  isAutoBidRuleUnsaved,
} from '~/pages/AdminApprovalFlow/models/autoBid'
import {
  convertProcessSetting,
  emptyProcessState,
} from '~/pages/AdminApprovalFlow/models/converter'
import {
  type MatrixState,
  type ProcessState,
  canSave,
  getAllCellKeys,
  matrixReducer,
} from '~/pages/AdminApprovalFlow/models/reducer'
import { saveProcess } from '~/pages/AdminApprovalFlow/models/saveProcess'
import { routePaths } from '~/routes/routePaths'

const INITIAL_STATE: MatrixState = new Map()

/** プロセスが保存可能か (全セル設定済み + auto_bid_rule 保存済み)。「保存して移動」用。 */
function isProcessSaveable(
  proc: ProcessState,
  autoBidRules: readonly AutoBidRule[],
): boolean {
  const def = PROCESS_DEFS.find(
    (d) => d.businessProcess === proc.businessProcess,
  )
  if (def === undefined) return false
  const allKeys = getAllCellKeys(def.businessProcess, def.areas)
  if (!canSave(proc, allKeys)) return false
  const rulesByArea = groupAutoBidRulesByArea(autoBidRules)
  for (const area of def.areas) {
    const key = makeNoSectionCellKey(def.businessProcess, area)
    const cell = proc.cells.get(key)
    if (
      cell?.mode === ApprovalMode.AUTO &&
      isAutoBidRuleUnsaved(rulesByArea, area)
    ) {
      return false
    }
  }
  return true
}

export const AdminApprovalFlowPage = (): React.ReactElement => {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()

  const { data: screenResponse } = useGetApprovalFlowScreen()

  const [matrixState, dispatch] = useReducer(matrixReducer, INITIAL_STATE)
  const matrixStateRef = useRef(matrixState)
  matrixStateRef.current = matrixState
  const { mutateAsync: saveAsync } = useSaveApprovalFlow()
  const [saving, setSaving] = useState(false)
  // 「保存して移動」で未設定のため保存できなかった回数 (業務処理ごと)。 該当の区画が
  // 開き、 「保存」 を押した時と同じ Alert・フォーカスで知らせる (AC-APF-011d)。
  const [leaveBlockedCounts, setLeaveBlockedCounts] = useState<
    ReadonlyMap<BusinessProcess, number>
  >(new Map())
  // 先頭の未設定の業務処理にだけフォーカスを送る (design: focusFirstSaveErr(blocked[0]))
  const [leaveFocusCounts, setLeaveFocusCounts] = useState<
    ReadonlyMap<BusinessProcess, number>
  >(new Map())
  const pendingLeaveFocusRef = useRef<BusinessProcess | undefined>(undefined)

  // サーバーから取得した設定を reducer に初期化。revalidate (invalidate 後) の際も再 init する。
  //
  // PROCESS_DEFS (入札 2 業務処理) を SoT としてループし、
  // backend response に含まれるプロセスは convertProcessSetting で変換、
  // 含まれないプロセス (= 新規組織等で DB に設定が無い) は emptyProcessState で初期化する。
  // これにより「screenResponse.processes が空配列を返す場合でも全プロセスカードが既定値のマトリクスで表示される」。
  // API 応答前 (screenResponse === undefined) は return して全カード「読み込み中」のまま (= 正しい挙動)。
  //
  // dirty なプロセスは上書きしない。per-プロセス保存/リセット時の invalidate/refetch が
  // 他プロセスの未保存編集を巻き戻さないため (AC-APF-011b / AC-APF-012a)。
  useEffect(() => {
    if (screenResponse === undefined) return
    // O(1) lookup のため businessProcess → setting の Map を作成
    const settingsByProcess = new Map(
      screenResponse.processes.map((s) => [s.businessProcess, s]),
    )
    for (const proc of PROCESS_DEFS) {
      const existing = matrixStateRef.current.get(proc.businessProcess)
      if (existing?.dirty) continue
      const setting = settingsByProcess.get(proc.businessProcess)
      const processState =
        setting !== undefined
          ? convertProcessSetting(setting)
          : emptyProcessState(proc.businessProcess)
      dispatch({ type: 'INIT_PROCESS', processState })
    }
  }, [screenResponse])

  // 未保存変更の離脱ガード (AC-011d)。dirty なプロセスが 1 つでもあれば遷移をブロックする。
  const anyDirty = useMemo(
    () => [...matrixState.values()].some((p) => p.dirty),
    [matrixState],
  )
  const {
    isBlocked: blocked,
    proceed,
    cancel: reset,
  } = useUnsavedGuard({
    isDirty: anyDirty,
  })

  /**
   * dirty な全プロセスを保存する。
   *
   * per-プロセス保存モデルのため、各プロセスを独立に保存する (= sticky footer 前提の
   * atomic 一括保存ではない)。保存可能 (= 全セル設定済み) なプロセスのみ
   * 保存し、保存できないプロセス (未設定あり) が残る場合は遷移を中止する
   * (= データ消失を避ける)。全 dirty プロセスを保存できた場合のみ true を返す。
   */
  const saveAllDirty = useCallback(async (): Promise<boolean> => {
    const dirtyProcs = [...matrixState.values()].filter((p) => p.dirty)
    const getAutoBidRules = (bp: BusinessProcess): readonly AutoBidRule[] =>
      screenResponse?.processes.find((p) => p.businessProcess === bp)
        ?.autoBidRules ?? []
    const saveable = dirtyProcs.filter((p) =>
      isProcessSaveable(p, getAutoBidRules(p.businessProcess)),
    )
    const unsaveable = dirtyProcs.filter(
      (p) => !isProcessSaveable(p, getAutoBidRules(p.businessProcess)),
    )

    const processLabel = (bp: BusinessProcess): string => {
      const def = PROCESS_DEFS.find((d) => d.businessProcess === bp)
      return def === undefined ? '' : t(def.i18nKey)
    }

    // 保存できるものはすべて保存する (1 つが失敗しても残りは続ける: AC-APF-011d)
    const results = await Promise.allSettled(
      saveable.map((proc) => saveProcess(proc, saveAsync, dispatch)),
    )

    let hasSaveError = false
    // 未設定で保存できない業務処理。 サーバーが未設定 (自動入札ルール未保存) を
    // 理由に拒否した業務処理 (FailedPrecondition) も未設定として扱う。
    const unsetProcs: BusinessProcess[] = unsaveable.map(
      (p) => p.businessProcess,
    )
    for (const [i, result] of results.entries()) {
      const bp = saveable[i].businessProcess
      if (result.status === 'fulfilled') {
        // 保存できた業務処理には AC-APF-011b の知らせを出す
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
        toaster.create({
          type: 'success',
          title: t('page:adminApprovalFlow.save.success', {
            process: processLabel(bp),
          }),
        })
      } else if (
        result.reason instanceof ConnectError &&
        result.reason.code === Code.FailedPrecondition
      ) {
        unsetProcs.push(bp)
      } else {
        hasSaveError = true
      }
    }

    if (hasSaveError) {
      // toaster は node10 解決下で eslint が any に潰すため局所 disable (ProcessAccordion と同様)
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
      toaster.create({
        type: 'error',
        title: t('page:adminApprovalFlow.leaveGuard.saveError'),
      })
    }
    if (unsetProcs.length > 0) {
      setLeaveBlockedCounts((prev) => {
        const next = new Map(prev)
        for (const bp of unsetProcs) next.set(bp, (prev.get(bp) ?? 0) + 1)
        return next
      })
      const first = PROCESS_DEFS.find((d) =>
        unsetProcs.includes(d.businessProcess),
      )?.businessProcess
      pendingLeaveFocusRef.current = first
    }
    // すべて保存できた時だけ移動する
    if (hasSaveError || unsetProcs.length > 0) return false
    return true
  }, [matrixState, screenResponse, saveAsync, t])

  // 離脱確認ダイアログが閉じてから、 先頭の未設定の業務処理へフォーカスを送る
  // (開いている間は背面にフォーカスが入らない)
  useEffect(() => {
    const target = pendingLeaveFocusRef.current
    if (blocked || target === undefined) return
    pendingLeaveFocusRef.current = undefined
    setLeaveFocusCounts((prev) =>
      new Map(prev).set(target, (prev.get(target) ?? 0) + 1),
    )
  }, [blocked])

  const handleSaveAndLeave = useCallback(async () => {
    setSaving(true)
    try {
      const ok = await saveAllDirty()
      if (ok) proceed()
      else reset()
    } finally {
      setSaving(false)
    }
  }, [saveAllDirty, proceed, reset])

  const handleDiscardAndLeave = useCallback(() => {
    for (const proc of matrixState.values()) {
      if (proc.dirty) {
        dispatch({
          type: 'RESET_PROCESS',
          businessProcess: proc.businessProcess,
        })
      }
    }
    proceed()
  }, [matrixState, proceed])

  // deep-link: ?process=<key> で該当プロセスを自動展開
  const deepLinkProcessKey = searchParams.get('process')

  const candidates = screenResponse?.candidates ?? []

  return (
    <PageLayout
      breadcrumbItems={[
        { label: t('common:nav.bidding') },
        { label: t('common:route.adminApprovalFlow'), bold: true },
      ]}
    >
      <PageBody>
        {/* ページ説明文 (design `.page-description`)。AC-APF-001 ※: 需要想定・需要調達計画提出の
            承認フロー設定は雛形側へ移ったため、前の置き場所を探しに来た人を雛形一覧へ送る。
            SPA 遷移なので離脱ガード (AC-011d) が効く */}
        <PageIntro
          description={
            <>
              {t('page:adminApprovalFlow.description')}
              <Box as="span" display="block" marginTop="1">
                {t('page:adminApprovalFlow.templateHint.body')}{' '}
                <Link asChild>
                  <RouterLink to={routePaths.adminWorkflowTemplates.path}>
                    {t('page:adminApprovalFlow.templateHint.link')}
                  </RouterLink>
                </Link>
              </Box>
            </>
          }
        />

        {/* AC-APF-011f: 反映タイミングの説明を画面上に表示 */}
        {/* 常設の説明 (操作を求めない) なので Alert でなく本文 (alert-placement.md §0) */}
        <Text textStyle="body" color="Text.highEmphasis">
          <Text as="span" fontWeight="semibold">
            {t('page:adminApprovalFlow.workflowActivationNotice.title')}
          </Text>{' '}
          {t('page:adminApprovalFlow.workflowActivationNotice.body')}
        </Text>

        {/* 入札 2 業務処理のアコーディオン (個別展開で高さが大きく伸びうるため PageScrollArea 内に配置) */}
        <PageScrollArea>
          <Flex flexDirection="column" gap="4">
            {PROCESS_DEFS.map((proc) => {
              const processKey = getProcessKey(proc.businessProcess)
              const defaultOpen = deepLinkProcessKey === processKey
              const autoBidRules =
                screenResponse?.processes.find(
                  (p) => p.businessProcess === proc.businessProcess,
                )?.autoBidRules ?? []
              return (
                <ProcessAccordion
                  key={proc.businessProcess}
                  autoBidRules={autoBidRules}
                  candidates={candidates}
                  defaultOpen={defaultOpen}
                  dispatch={dispatch}
                  leaveBlockedCount={
                    leaveBlockedCounts.get(proc.businessProcess) ?? 0
                  }
                  leaveFocusCount={
                    leaveFocusCounts.get(proc.businessProcess) ?? 0
                  }
                  processDef={proc}
                  processState={matrixState.get(proc.businessProcess)}
                />
              )
            })}
          </Flex>
        </PageScrollArea>
      </PageBody>

      {/* 未保存離脱ガードの 3 択ダイアログ (AC-011d) */}
      <UnsavedLeaveDialog
        isOpen={blocked}
        onCancel={reset}
        onDiscardAndLeave={handleDiscardAndLeave}
        onSaveAndLeave={() => void handleSaveAndLeave()}
        saving={saving}
      />
    </PageLayout>
  )
}

/** BusinessProcess enum 値 → deep-link key 文字列の対応 */
function getProcessKey(bp: BusinessProcess): string {
  switch (bp) {
    case BusinessProcess.SPOT_BID:
      return 'spot-bid'
    case BusinessProcess.INTRADAY_BID:
      return 'intraday-bid'
    default:
      return ''
  }
}
